import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";

export interface SubscriptionState {
  isPro: boolean;
  loading: boolean;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  refresh: () => void;
}

const OVERRIDE_KEY = "savey_sub_override";

type OverrideState = {
  isPro: boolean;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

function readOverride(): OverrideState | null {
  try {
    const raw = localStorage.getItem(OVERRIDE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OverrideState;
  } catch {
    return null;
  }
}

export function useSubscription(): SubscriptionState {
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const onOverrideChange = () => setTick((t) => t + 1);
    window.addEventListener("savey:sub-override-changed", onOverrideChange);
    window.addEventListener("storage", onOverrideChange);
    return () => {
      window.removeEventListener("savey:sub-override-changed", onOverrideChange);
      window.removeEventListener("storage", onOverrideChange);
    };
  }, []);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Apply local override first (JJT testing tool) — bypasses real DB.
        const override = readOverride();
        if (override) {
          if (!cancelled) {
            setIsPro(override.isPro);
            setStatus(override.status);
            setCurrentPeriodEnd(override.currentPeriodEnd);
            setCancelAtPeriodEnd(override.cancelAtPeriodEnd);
          }
          return;
        }

        // Don't flip loading back to true on refetches — keeps UI from flickering
        // between Pro/Free states. Initial load is covered by useState(true).
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        const user = sessionData.session?.user;
        if (!user) {
          if (!cancelled) {
            setIsPro(false);
            setStatus(null);
            setCurrentPeriodEnd(null);
            setCancelAtPeriodEnd(false);
          }
          return;
        }

        const { data: isAdmin, error: adminError } = await supabase.rpc("has_role", {
          _user_id: user.id,
          _role: "admin",
        });
        if (adminError) throw adminError;

        if (isAdmin) {
          if (!cancelled) {
            setIsPro(true);
            setStatus("admin");
            setCurrentPeriodEnd(null);
            setCancelAtPeriodEnd(false);
          }
          return;
        }

        const env = getPaddleEnvironment();
        const { data, error: subscriptionError } = await supabase
          .from("subscriptions")
          .select("status, current_period_end, cancel_at_period_end")
          .eq("user_id", user.id)
          .eq("environment", env)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subscriptionError) throw subscriptionError;
        if (cancelled) return;

        if (!data) {
          setIsPro(false);
          setStatus(null);
          setCurrentPeriodEnd(null);
          setCancelAtPeriodEnd(false);
          return;
        }

        const now = new Date();
        const end = data.current_period_end ? new Date(data.current_period_end) : null;
        const active =
          (["active", "trialing", "past_due"].includes(data.status) && (!end || end > now)) ||
          (data.status === "canceled" && end && end > now);

        setIsPro(!!active);
        setStatus(data.status);
        setCurrentPeriodEnd(data.current_period_end);
        setCancelAtPeriodEnd(!!data.cancel_at_period_end);
      } catch (error) {
        console.error("Failed to load subscription state", error);
        // Keep the last known state if a transient auth/database request fails.
        if (cancelled) return;
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tick]);

  return {
    isPro,
    loading,
    status,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    refresh,
  };
}
